import React, { Component, ReactNode } from 'react';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { AuthLogo } from './AuthLogo';

interface AuthErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorCategory?: ErrorCategory;
  retryCount: number;
}

interface AuthErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
  readonly onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  readonly maxRetries?: number;
}

type ErrorCategory =
  | 'network'
  | 'configuration'
  | 'authentication'
  | 'permission'
  | 'timeout'
  | 'unknown';

interface ErrorDetails {
  category: ErrorCategory;
  title: string;
  description: string;
  actionable: boolean;
  recoveryOptions: RecoveryOption[];
}

interface RecoveryOption {
  label: string;
  action: () => void;
  primary?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * Error pattern definitions for categorization
 * Maps error categories to their associated keywords for pattern matching
 *
 * Note: Order matters! More specific patterns should be checked first to avoid
 * false matches. For example, 'permission' should be checked before 'authentication'
 * to prevent "permission_error" from matching "auth" in "authentication".
 */
const ERROR_PATTERNS: ReadonlyMap<ErrorCategory, readonly string[]> = new Map([
  ['network', ['network', 'fetch', 'connection'] as const],
  ['configuration', ['config', 'client_id', 'authority'] as const],
  ['permission', ['permission', 'access', 'forbidden'] as const],
  ['timeout', ['timeout', 'expired'] as const],
  ['authentication', ['auth', 'login', 'token'] as const],
] as const);

/**
 * Check if an error message matches any patterns for a specific category
 *
 * @param message - Lowercase error message to check
 * @param patterns - Array of patterns to match against
 * @returns True if any pattern matches the message
 */
function matchesErrorPatterns(
  message: string,
  patterns: readonly string[],
): boolean {
  return patterns.some((pattern) => message.includes(pattern));
}

/**
 * Categorize errors based on error message pattern matching
 *
 * This function uses a pattern-matching approach to categorize authentication errors
 * into predefined categories. It reduces cyclomatic complexity by using a Map-based
 * lookup instead of sequential if-statements.
 *
 * The categorization is based on keyword patterns found in the error message:
 * - network: Connection-related errors (network, fetch, connection)
 * - configuration: Setup/config errors (config, client_id, authority)
 * - authentication: Auth flow errors (auth, login, token)
 * - permission: Access control errors (permission, access, forbidden)
 * - timeout: Time-related errors (timeout, expired)
 * - unknown: Fallback for unrecognized error patterns
 *
 * @param error - The error object to categorize
 * @returns ErrorCategory enum value representing the error type
 */
function categorizeError(error: Error): ErrorCategory {
  const message = error.message.toLowerCase();

  // Iterate through error patterns to find the first match
  for (const [category, patterns] of ERROR_PATTERNS) {
    if (matchesErrorPatterns(message, patterns)) {
      return category;
    }
  }

  // Default to 'unknown' if no patterns match
  return 'unknown';
}

/**
 * Error configuration mapping
 */
interface ErrorConfig {
  title: string;
  description: string;
  actionable: boolean;
  retryLabel?: string;
}

const ERROR_CONFIGS: Record<ErrorCategory, ErrorConfig> = {
  network: {
    title: 'Connection Problem',
    description:
      'Unable to connect to authentication services. Please check your internet connection and try again.',
    actionable: true,
    retryLabel: 'Try Again',
  },
  configuration: {
    title: 'Configuration Error',
    description:
      "There's an issue with the authentication configuration. Please contact your system administrator.",
    actionable: false,
  },
  authentication: {
    title: 'Authentication Failed',
    description:
      'Unable to complete authentication. This might be due to invalid credentials or session expiration.',
    actionable: true,
    retryLabel: 'Try Signing In Again',
  },
  permission: {
    title: 'Access Denied',
    description:
      "You don't have permission to access this application. Please contact your administrator.",
    actionable: false,
  },
  timeout: {
    title: 'Request Timeout',
    description:
      'The authentication request took too long to complete. Please try again.',
    actionable: true,
    retryLabel: 'Try Again',
  },
  unknown: {
    title: 'Something Went Wrong',
    description:
      'An unexpected error occurred during authentication. Please try again or contact support if the problem persists.',
    actionable: true,
    retryLabel: 'Try Again',
  },
};

/**
 * Create recovery options based on error configuration
 */
function createRecoveryOptions(
  config: ErrorConfig,
  onRetry: () => void,
): RecoveryOption[] {
  const baseOptions: RecoveryOption[] = [
    {
      label: 'Go to Home',
      action: () => (window.location.href = '/'),
      icon: HomeIcon,
    },
  ];

  if (config.actionable && config.retryLabel) {
    return [
      {
        label: config.retryLabel,
        action: onRetry,
        primary: true,
        icon: ArrowPathIcon,
      },
      ...baseOptions,
    ];
  }

  return baseOptions;
}

/**
 * Get error details based on category
 */
function getErrorDetails(
  _error: Error,
  category: ErrorCategory,
  onRetry: () => void,
): ErrorDetails {
  const config = ERROR_CONFIGS[category];

  return {
    category,
    title: config.title,
    description: config.description,
    actionable: config.actionable,
    recoveryOptions: createRecoveryOptions(config, onRetry),
  };
}

/**
 * Button style variants
 */
const BUTTON_STYLES = {
  base: 'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
  primary: ' bg-True-Blue text-white hover:bg-Blue-Text focus:ring-True-Blue',
  secondary: ' bg-gray-100 text-Charcoal hover:bg-gray-200 focus:ring-gray-300',
  disabled: ' bg-gray-300 text-gray-500 cursor-not-allowed',
} as const;

/**
 * Recovery Button Component
 */
const RecoveryButton: React.FC<{
  option: RecoveryOption;
  canRetry: boolean;
}> = ({ option, canRetry }) => {
  const Icon = option.icon;
  const isRetryOption =
    option.label.toLowerCase().includes('try') ??
    option.label.toLowerCase().includes('again');
  const isDisabled = isRetryOption && !canRetry;

  const getButtonStyle = () => {
    if (isDisabled) return BUTTON_STYLES.base + BUTTON_STYLES.disabled;
    if (option.primary) return BUTTON_STYLES.base + BUTTON_STYLES.primary;
    return BUTTON_STYLES.base + BUTTON_STYLES.secondary;
  };

  return (
    <button
      onClick={option.action}
      disabled={isDisabled}
      className={getButtonStyle()}
      aria-label={option.label}
    >
      {Icon && <Icon className="w-5 h-5" aria-hidden="true" />}
      {option.label}
    </button>
  );
};

/**
 * Error Display Component
 */
const ErrorDisplay: React.FC<{
  error: Error;
  errorDetails: ErrorDetails;
  retryCount: number;
  maxRetries: number;
}> = ({ error, errorDetails, retryCount, maxRetries }) => {
  const canRetry = errorDetails.actionable && retryCount < maxRetries;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-True-Blue to-Blue-Text p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <AuthLogo size="lg" withBottomMargin />
        </div>

        {/* Error Icon */}
        <div className="mb-6">
          <ExclamationTriangleIcon
            className="w-16 h-16 text-red-500 mx-auto"
            aria-hidden="true"
          />
        </div>

        {/* Error Content */}
        <div className="mb-8">
          <h1 className="text-xl font-bold text-Charcoal mb-3">
            {errorDetails.title}
          </h1>
          <p className="text-Informational-Text text-sm leading-relaxed">
            {errorDetails.description}
          </p>

          {retryCount > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              Attempt {retryCount + 1} of {maxRetries + 1}
            </p>
          )}
        </div>

        {/* Recovery Actions */}
        <div className="space-y-3">
          {errorDetails.recoveryOptions.map((option) => (
            <RecoveryButton
              key={option.label}
              option={option}
              canRetry={canRetry}
            />
          ))}
        </div>

        {/* Technical Details (Development) */}
        {import.meta.env.DEV && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
              Technical Details
            </summary>
            <div className="mt-2 p-3 bg-gray-50 rounded text-xs font-mono text-gray-700 overflow-auto max-h-32">
              <div>
                <strong>Error:</strong> {error.message}
              </div>
              <div>
                <strong>Category:</strong> {errorDetails.category}
              </div>
              <div>
                <strong>Stack:</strong> {error.stack?.slice(0, 200)}...
              </div>
            </div>
          </details>
        )}

        {/* Accessibility */}
        <div className="sr-only">
          Error occurred: {errorDetails.title}. {errorDetails.description}
          {canRetry
            ? ' You can try again.'
            : ' Please contact support for assistance.'}
        </div>
      </div>
    </div>
  );
};

/**
 * Authentication Error Boundary Component
 *
 * Provides error handling for authentication flows with:
 * - Error categorization and user-friendly messages
 * - Actionable recovery options with retry mechanisms
 * - Fallback authentication options
 * - Mobile-optimized responsive design
 * - Full accessibility support
 */
export class AuthErrorBoundary extends Component<
  AuthErrorBoundaryProps,
  AuthErrorBoundaryState
> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<AuthErrorBoundaryState> {
    const errorCategory = categorizeError(error);
    return {
      hasError: true,
      error,
      errorCategory,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      '🚨 Authentication Error Boundary caught an error:',
      error,
      errorInfo,
    );

    this.setState({
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Report to monitoring service in production
    if (!import.meta.env.DEV) {
      // Report authentication errors to monitoring service
      // This integrates with the existing Sentry setup in the application
      try {
        console.error('Authentication Error:', {
          error: error.message,
          stack: error.stack,
          category: this.state.errorCategory,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
      } catch (reportingError) {
        console.error('Failed to report error:', reportingError);
      }
    }
  }

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        errorCategory: undefined,
        retryCount: retryCount + 1,
      });
    }
  };

  render() {
    const { children, fallback, maxRetries = 3 } = this.props;
    const { hasError, error, errorCategory, retryCount } = this.state;

    if (hasError && error && errorCategory) {
      const errorDetails = getErrorDetails(
        error,
        errorCategory,
        this.handleRetry,
      );

      if (fallback) {
        return fallback;
      }

      return (
        <ErrorDisplay
          error={error}
          errorDetails={errorDetails}
          retryCount={retryCount}
          maxRetries={maxRetries}
        />
      );
    }

    return children;
  }
}

export default AuthErrorBoundary;
